// ══════════════════════════════════════════════════════════════════════════════
// PriestJoinPage.js — Priest portal for joining a puja session
// Access: /?priest=<booking_id>&secret=<admin_secret>
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import PujaRoom from "./PujaRoom";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

const C = {
  bg: "#06030A", bg2: "#0D0818", bg3: "#130D22",
  saffron: "#E8832A", gold: "#C9953A", cream: "#FFF5E0",
  text: "#EDE0C8", dim: "#8A7558",
  border: "rgba(201,149,58,0.25)",
};

export default function PriestJoinPage({ bookingId, secret }) {
  const [state,       setState]       = useState("loading"); // loading | ready | joining | room | error
  const [bookingInfo, setBookingInfo] = useState(null);
  const [agoraConfig, setAgoraConfig] = useState(null);
  const [errMsg,      setErrMsg]      = useState("");

  // Fetch booking details to show priest what's coming
  useEffect(() => {
    if (!bookingId || !secret) {
      setState("error");
      setErrMsg("Missing booking ID or secret in URL.");
      return;
    }
    fetch(`${API}/puja/booking/${bookingId}`)
      .then(r => r.json())
      .then(data => {
        if (data.detail) throw new Error(data.detail);
        setBookingInfo(data);
        setState("ready");
      })
      .catch(e => {
        setState("error");
        setErrMsg(e.message || "Could not load booking details.");
      });
  }, [bookingId, secret]);

  const handleJoin = async () => {
    setState("joining");
    setErrMsg("");
    try {
      const res  = await fetch(
        `${API}/puja/priest-token/${bookingId}?secret=${encodeURIComponent(secret)}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Failed to get token");
      setAgoraConfig({
        appId:      data.app_id,
        token:      data.token,
        channel:    data.channel,
        uid:        data.uid,
        priestUid:  data.uid,       // priest's own uid IS the priestUid
        priestName: bookingInfo?.priest_name || "Priest",
      });
      setState("room");
    } catch (e) {
      setState("ready");
      setErrMsg(e.message || "Could not join. Check secret and try again.");
    }
  };

  // Full-screen room when joined
  if (state === "room" && agoraConfig && bookingInfo) {
    return (
      <PujaRoom
        booking={{
          puja_name:  bookingInfo.puja_name,
          puja_emoji: "🔱",
        }}
        agoraConfig={agoraConfig}
        onLeave={() => { setState("ready"); setAgoraConfig(null); }}
      />
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: C.bg,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px",
    }}>
      <div style={{
        width: "100%", maxWidth: "480px",
        background: C.bg2, border: `1px solid ${C.border}`,
        borderRadius: "20px", padding: "40px 36px",
        boxShadow: "0 0 60px rgba(232,131,42,0.08)",
      }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{
            fontSize: "3rem", marginBottom: "12px",
            filter: "drop-shadow(0 0 16px rgba(232,131,42,0.5))",
          }}>🔱</div>
          <div style={{
            fontFamily: "'Cinzel Decorative', serif",
            fontSize: "1.1rem", color: C.saffron, marginBottom: "6px",
          }}>
            Priest Portal
          </div>
          <div style={{ fontSize: "0.82rem", color: C.dim }}>
            VedSanga · Live Puja
          </div>
        </div>

        {/* Loading */}
        {state === "loading" && (
          <div style={{ textAlign: "center", color: C.dim, fontSize: "0.9rem" }}>
            Loading booking details…
          </div>
        )}

        {/* Error */}
        {state === "error" && (
          <div style={{
            background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
            borderRadius: "12px", padding: "16px 20px",
            fontSize: "0.85rem", color: "#fca5a5", textAlign: "center",
          }}>
            {errMsg}
          </div>
        )}

        {/* Ready to join */}
        {(state === "ready" || state === "joining") && bookingInfo && (
          <>
            {/* Booking details card */}
            <div style={{
              background: C.bg3, border: `1px solid ${C.border}`,
              borderRadius: "14px", padding: "20px", marginBottom: "24px",
            }}>
              <div style={{
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: "0.92rem", color: C.cream, marginBottom: "14px",
                paddingBottom: "10px", borderBottom: `1px solid rgba(201,149,58,0.12)`,
              }}>
                {bookingInfo.puja_name}
              </div>
              {[
                ["Date",     `${bookingInfo.preferred_date} · ${bookingInfo.preferred_time} IST`],
                ["Tier",     bookingInfo.tier ? bookingInfo.tier.charAt(0).toUpperCase() + bookingInfo.tier.slice(1) : "—"],
                ["Type",     bookingInfo.booking_type === "family" ? "Family" : "Individual"],
                ["Devotee",  bookingInfo.name || "—"],
                ...(bookingInfo.sankalpa ? [["Sankalpa", bookingInfo.sankalpa]] : []),
                ...(bookingInfo.gotra    ? [["Gotra",    bookingInfo.gotra]]    : []),
              ].map(([k, v]) => (
                <div key={k} style={{
                  display: "flex", justifyContent: "space-between",
                  padding: "7px 0", borderBottom: `1px solid rgba(201,149,58,0.07)`,
                  fontSize: "0.84rem",
                }}>
                  <span style={{ color: C.dim }}>{k}</span>
                  <span style={{ color: C.text, maxWidth: "60%", textAlign: "right", lineHeight: "1.5" }}>{v}</span>
                </div>
              ))}
            </div>

            {/* Setup reminder */}
            <div style={{
              background: "rgba(232,131,42,0.05)", border: "1px solid rgba(232,131,42,0.18)",
              borderRadius: "10px", padding: "14px 16px", marginBottom: "22px",
              fontSize: "0.78rem", color: C.dim, lineHeight: "1.9",
            }}>
              <div style={{ color: C.saffron, fontFamily: "'Cinzel Decorative', serif",
                fontSize: "0.7rem", marginBottom: "8px", letterSpacing: "0.05em" }}>
                CAMERA SETUP — BEFORE YOU JOIN
              </div>
              <div style={{ color: C.text, marginBottom: "10px", fontSize: "0.8rem", lineHeight: "1.7" }}>
                Place your camera <strong style={{ color: C.gold }}>3–4 feet away</strong> at a{" "}
                <strong style={{ color: C.gold }}>45° angle</strong> to your side.
                This single frame will show your face, your hands performing the ritual,
                and the deity idol — all together. Devotees see everything as one divine scene.
              </div>
              ✓ Camera at shoulder height, angled toward you and the altar<br />
              ✓ Deity idol visible in the same frame as your hands<br />
              ✓ Warm light source (lamp/diya) in front — no backlight<br />
              ✓ Microphone working — test before joining<br />
              ✓ Join 5 minutes early to frame the shot correctly
            </div>

            {errMsg && (
              <div style={{
                background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)",
                borderRadius: "8px", padding: "10px 14px", marginBottom: "16px",
                fontSize: "0.8rem", color: "#fca5a5",
              }}>
                {errMsg}
              </div>
            )}

            <button
              onClick={handleJoin}
              disabled={state === "joining"}
              style={{
                width: "100%", padding: "15px",
                background: state === "joining"
                  ? "rgba(232,131,42,0.3)"
                  : "linear-gradient(135deg, #E8832A, #C9953A)",
                border: "none", borderRadius: "12px",
                color: "white",
                fontFamily: "'Cinzel Decorative', serif",
                fontSize: "0.92rem", letterSpacing: "0.04em",
                cursor: state === "joining" ? "wait" : "pointer",
                transition: "all 0.2s",
                boxShadow: state === "joining" ? "none" : "0 6px 24px rgba(232,131,42,0.35)",
              }}
            >
              {state === "joining" ? "Connecting…" : "🔱 Join as Priest"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
